import { i18n } from '@/i18n';

/**
 * Helper for internationalized date formatting
 * Provides consistent date formats across the application with i18n support
 */
class DateI18nHelper {

	private static instance: DateI18nHelper;

	static getInstance (): DateI18nHelper {
		if (!DateI18nHelper.instance) {
			DateI18nHelper.instance = new DateI18nHelper();
		}
		return DateI18nHelper.instance;
	}

	private get locale () { return i18n.global.locale as unknown as string }

	private dateTime (date: Date): string {
		return i18n.global.t('common.dateAtTime', {
			date: date.toLocaleDateString(this.locale),
			time: date.toLocaleTimeString(this.locale),
		});
	}

	createdOn (date: Date | string): string {
		if (typeof date === 'string') date = new Date(date);

		return i18n.global.t('date.formats.createdOn', { date: this.dateTime(date) });
	}

	dateRange (startDate: Date | string, endDate: Date | string): string {
		if (typeof startDate === 'string') startDate = new Date(startDate);
		if (typeof endDate === 'string') endDate = new Date(endDate);

		return i18n.global.t('date.formats.fromTo', {
			from: i18n.global.t('date.from'),
			startDate: startDate.toLocaleDateString(this.locale),
			to: i18n.global.t('date.to'),
			endDate: endDate.toLocaleDateString(this.locale),
		});
	}

	durationFrom (startDate: Date, duration?: string): string {
		return i18n.global.t('date.formats.durationFrom', {
			startDate: this.dateTime(startDate),
			duration,
		});
	}

	documentHistory (type: 'deposit' | 'viewed' | 'detailViewed' | 'downloaded' | 'signed', date: Date | string): string {
		if (typeof date === 'string') date = new Date(date);

		const typeKey = `date.document.${type}`;
		return i18n.global.t('date.formats.documentHistory', {
			label: i18n.global.t(typeKey),
			date: this.dateTime(date),
		});
	}

	signatureDate (date: Date | string, context?: string): string {
		if (typeof date === 'string') date = new Date(date);

		const formattedDate = this.dateTime(date);
		return context
			? i18n.global.t('date.formats.signatureWithContext', {
				context,
				date: formattedDate,
			})
			: formattedDate;
	}

	requestSentOn (date: Date): string {
		return i18n.global.t('date.formats.requestSentOn', { date: this.dateTime(date) });
	}

}

/**
 * Composable for using date i18n helper
 */
export function useDateI18n () {
	return DateI18nHelper.getInstance();
}
